import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, OnDestroy, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { UserType } from '../../types';
import { GlobalDataService, SessionData } from '../global-data/global-data.service';
import { RawAuthResponse } from './auth.service';

@Injectable({ providedIn: 'root' })
export class SamSessionService implements OnDestroy {
  private channel?: BroadcastChannel;
  private refreshSequence = 0;
  private readonly remoteSignOut = signal(false);
  readonly signedOutInAnotherTab = this.remoteSignOut.asReadonly();
  private lastPrincipal?: { userId: number; userType: string; storeId: number | null };
  constructor(private http: HttpClient, private global: GlobalDataService) {
    if (typeof BroadcastChannel !== 'undefined') {
      this.channel = new BroadcastChannel('sam-session');
      this.channel.onmessage = event => {
        if (event.data?.type === 'signed-out') {
          const current = this.global.getSessionData().value;
          if (current.sessionVersion && current.sessionVersion === event.data.sessionVersion) {
            void this.refresh(current.sessionVersion);
          }
        } else if (event.data === 'changed') void this.refresh();
      };
    }
  }

  async initialize(): Promise<void> { await this.refresh(); }

  ngOnDestroy(): void {
    this.refreshSequence++;
    this.channel?.close();
  }

  async refresh(signedOutVersion?: string): Promise<void> {
    // Keep the mounted page detached from later sign-ins until the user leaves it.
    if (this.remoteSignOut()) return;
    const sequence = ++this.refreshSequence;
    const generation = this.global.getAuthenticationGeneration();
    try {
      const response = await firstValueFrom(this.http.get<RawAuthResponse>('/api/User/Session', { withCredentials: true }));
      if (sequence !== this.refreshSequence || generation !== this.global.getAuthenticationGeneration()) return;
      this.install(response);
    } catch (error) {
      if (sequence !== this.refreshSequence || generation !== this.global.getAuthenticationGeneration()) return;
      if (error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403)) {
        const current = this.global.getSessionData().value;
        if (signedOutVersion && current.sessionVersion !== signedOutVersion) return;
        if (signedOutVersion) this.remoteSignOut.set(true);
        this.global.clearSessionData();
      }
    }
  }

  signOut(): Promise<boolean> {
    const sessionVersion = this.global.getSessionData().value.sessionVersion;
    return firstValueFrom(this.http.post<void>('/api/User/SignOut', {}, { withCredentials: true })).then(() => {
      this.channel?.postMessage({ type: 'signed-out', sessionVersion });
      if (this.global.getSessionData().value.sessionVersion !== sessionVersion) return false;
      this.refreshSequence++;
      this.global.clearSessionData();
      return true;
    }).catch(error => {
      if (error?.status === 409 && error?.error?.code === 'sam_session_changed') return this.refresh().then(() => false);
      throw error;
    });
  }

  authenticated(response: RawAuthResponse): void {
    this.refreshSequence++;
    this.install(response);
    this.channel?.postMessage('changed');
  }

  private install(response: RawAuthResponse): void {
    const current = this.global.getSessionData().value;
    const isSameHq = this.lastPrincipal?.userId === response.id &&
      this.lastPrincipal.userType === response.userType && response.userType === UserType.ActiveDirectory;
    const value: SessionData = {
      initialized: true, token: '', userId: response.id, userType: response.userType,
      store: response.store ?? (isSameHq ? current.store : null),
      storeImpersonation: isSameHq && current.storeImpersonation,
      roles: response.roles ?? (isSameHq ? current.roles : []), editorStoreIds: response.editorStoreIds, matchedStoreId: response.matchedStoreId, sessionVersion: response.sessionVersion,
      csrfToken: response.csrfToken, expiresAt: response.expiresAt,
    };
    const principal = { userId: value.userId, userType: value.userType, storeId: value.store?.id ?? null };
    const prior = this.lastPrincipal;
    if (prior && (prior.userId !== principal.userId || prior.userType !== principal.userType ||
        prior.userType === UserType.AgillicAdvisor && prior.storeId !== principal.storeId)) {
      this.global.clearSessionData();
      if (typeof window !== 'undefined') window.location.replace(
        '/landing'
      );
      return;
    }
    this.lastPrincipal = principal;
    this.global.setSessionData(value);
  }

}
